import React, { useEffect, useState, useMemo, useRef } from "react";
import { fetchLocations, addLocation, fetchSchedulesInRange, updateLocation, deleteLocation } from "../api";
import { useAuth } from '../context/AuthContext';
import Papa from 'papaparse';
import { 
    FiMapPin, FiSearch, FiPlus, FiEdit2, FiTrash2, FiDownload,
    FiUpload, FiX, FiUsers, FiMap, FiFilter, FiCheckCircle
} from 'react-icons/fi';

const getFirstName = (nameOrObject) => {
    if (!nameOrObject) return ''; // Handles null, undefined, or empty array

    let actualObject = nameOrObject;

    if (Array.isArray(nameOrObject) && nameOrObject.length > 0) {
        actualObject = nameOrObject[0]; // Get the first item from the array
    }

    if (typeof actualObject === 'object' && actualObject !== null) {
        if (actualObject.firstName) {
            return `${actualObject.firstName} ${actualObject.lastName || ''}`.trim();
        }
        if (actualObject.name) {
            const parts = actualObject.name.trim().split(' ');
            return parts.length > 0 ? parts[0] : '';
        }
        return ''; // Object but no recognizable name property
    }

    if (typeof actualObject === 'string') {
        const parts = actualObject.trim().split(' ');
        return parts.length > 0 ? parts[0] : '';
    }

    return ''; // Handle any other unexpected types
};

const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday;
};

// Generate unique color for each worker based on their name
const getWorkerColor = (name) => {
    const colors = [
        { from: '#a855f7', to: '#9333ea' }, // purple
        { from: '#ec4899', to: '#db2777' }, // pink
        { from: '#f43f5e', to: '#e11d48' }, // rose
        { from: '#f97316', to: '#ea580c' }, // orange
        { from: '#f59e0b', to: '#d97706' }, // amber
        { from: '#eab308', to: '#ca8a04' }, // yellow
        { from: '#84cc16', to: '#65a30d' }, // lime
        { from: '#10b981', to: '#059669' }, // emerald
        { from: '#14b8a6', to: '#0d9488' }, // teal
        { from: '#06b6d4', to: '#0891b2' }, // cyan
        { from: '#0ea5e9', to: '#0284c7' }, // sky
        { from: '#6366f1', to: '#4f46e5' }, // indigo
        { from: '#8b5cf6', to: '#7c3aed' }, // violet
        { from: '#d946ef', to: '#c026d3' }, // fuchsia
    ];
    
    // Generate a hash from the name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Use the hash to pick a color
    const index = Math.abs(hash) % colors.length;
    return colors[index];
};

export default function LocationPage() {
    const [locations, setLocations] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedLocation, setSelectedLocation] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [editLocation, setEditLocation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const { user, token, isLoading: isAuthLoading } = useAuth();
    const fileInputRef = useRef(null);
    const modalRef = useRef(null);

    const [form, setForm] = useState({
        name: '',
        street: '',
        city: '',
        postalCode: '',
        country: '',
        workType: '',
    });
    const [formError, setFormError] = useState('');

    useEffect(() => {
        const loadLocations = async () => {
            setLoading(true);
            setApiError(null);
            if (!token) {
                setApiError("Authentication token missing. Please log in.");
                setLoading(false);
                return;
            }

            try {
                const res = await fetchLocations();
                setLocations(res.data.data);
            } catch (err) {
                console.error("Failed to fetch locations:", err);
                setApiError(
                    err.response?.data?.message ||
                    "Failed to load locations. Please try again."
                );
            } finally {
                setLoading(false);
            }
        };

        if (token && !isAuthLoading) {
            loadLocations();
        } else if (!token && !isAuthLoading) {
            setApiError("You are not logged in. Please log in to view locations.");
        }
    }, [token, isAuthLoading]);

    useEffect(() => {
        const loadSchedules = async () => {
            if (!token || isAuthLoading) {
                setSchedules([]);
                return;
            }

            try {
                const currentYear = new Date().getFullYear();
                const startOfYear = new Date(currentYear, 0, 1);
                const endOfYear = new Date(currentYear, 11, 31);
                const res = await fetchSchedulesInRange(startOfYear, endOfYear);
                setSchedules(res.data.data);
            } catch (err) {
                console.error("Failed to fetch schedules in range:", err);
                setApiError("Failed to load schedules. Please try again.");
            }
        };

        if (!isAuthLoading && token) {
            loadSchedules();
        } else if (!token && !isAuthLoading) {
            setSchedules([]);
        }
    }, [token, isAuthLoading]);

   useEffect(() => {
  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      handleCloseModal();
    }
  };

  if (isModalOpen || isEditModalOpen) {
    document.addEventListener("mousedown", handleClickOutside);
  }

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [isModalOpen, isEditModalOpen]);


    const filteredLocations = useMemo(() => {
        return locations.filter((loc) =>
            (loc.name|| '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (loc.address?.street || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (loc.address?.city|| '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (loc.address?.postalCode||'').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (loc.address?.country||'').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (loc.workType||'').toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [locations, searchTerm]);

    const scheduledWorkers = useMemo(() => {
        if (!schedules || schedules.length === 0) {
            // console.log("No schedules available");
            return [];
        }

        if (!selectedLocation) {
            const allWorkers = schedules
                .filter(s => s.careWorker && s.location)
                .map(s => ({
                    name: getFirstName(s.careWorker),
                    location: s.location?.name || 'Unknown'
                }));
            const uniqueWorkers = [...new Set(allWorkers.map(w => w.name))].filter(name => name);
            return uniqueWorkers;
        } else {
            const filteredSchedules = schedules.filter(s => {
                if (!s.location || !s.location.name) {
                    console.warn(`Schedule missing location data:`, s);
                    return false;
                }
                return s.location.name === selectedLocation;
            });
            const workersForLocation = filteredSchedules
                .filter(s => s.careWorker)
                .map(s => getFirstName(s.careWorker));
            const uniqueWorkersForLocation = [...new Set(workersForLocation)].filter(name => name);
            return uniqueWorkersForLocation;
        }
    }, [schedules, selectedLocation]);

    const handleOpenModal = () => {
        setIsModalOpen(true);
        setApiError(null);
        setSuccessMessage("");
        setFormError("");
        setForm({
            name: '',
            street: '',
            city: '',
            postalCode: '',
            country: '',
            workType: '',
        });
        setEditLocation(null);
    };

    const handleEditLocation = (location) => {
        setIsEditModalOpen(true);
        setApiError(null);
        setSuccessMessage("");
        setFormError("");
        setForm({
            name: location.name || '',
            street: location.address?.street || '',
            city: location.address?.city || '',
            postalCode: location.address?.postalCode || '',
            country: location.address?.country || '',
            workType: location.workType || '',
        });
        setEditLocation(location);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setIsEditModalOpen(false);
        setIsDeleteModalOpen(false);
        setApiError(null);
        setFormError('');
        setEditLocation(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleDeleteLocation = async () => {
        if (!editLocation) return;
        if (!token) {
            setApiError("You must be logged in to delete a location.");
            return;
        }

        try {
            await deleteLocation(editLocation._id);
            setLocations((prev) => prev.filter((loc) => loc._id !== editLocation._id));
            setSuccessMessage("Location deleted successfully!");
            handleCloseModal();
        } catch (err) {
            console.error("Failed to delete location:", {
                message: err.message,
                status: err.response?.status,
                data: err.response?.data,
            });
            setApiError(err.response?.data?.msg || "Failed to delete location. Please try again.");
        } finally {
            setLoading(false);
            setTimeout(() => {
                setApiError(null);
                setSuccessMessage("");
            }, 5000);
        }
        // setIsDeleteModalOpen(true);
        setIsDeleteModalOpen(false)
    };

    const confirmDeleteLocation = async () => {
        setLoading(true);
        setApiError(null);

        // try {
        //     await deleteLocation(editLocation._id);
        //     setLocations((prev) => prev.filter((loc) => loc._id !== editLocation._id));
        //     setSuccessMessage("Location deleted successfully!");
        //     handleCloseModal();
        // } catch (err) {
        //     console.error("Failed to delete location:", {
        //         message: err.message,
        //         status: err.response?.status,
        //         data: err.response?.data,
        //     });
        //     setApiError(err.response?.data?.msg || "Failed to delete location. Please try again.");
        // } finally {
        //     setLoading(false);
        //     setTimeout(() => {
        //         setApiError(null);
        //         setSuccessMessage("");
        //     }, 5000);
        // }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        setApiError(null);
        if (!token) {
            setApiError("You must be logged in to add or update a location.");
            setLoading(false);
            return;
        }

        if (!form.name || !form.street || !form.city || !form.postalCode || !form.country || !form.workType) {
            if (!form.workType) {
                setFormError('Work type is required.');
            } else {
                setFormError('All fields are required.');
            }
            return;
        }

        setLoading(true);

        const locationData = {
            name: form.name,
            address: {
                street: form.street,
                city: form.city,
                postalCode: form.postalCode,
                country: form.country,
            },
            workType: form.workType,
            isCustom: true,
        };


        try {
            if (editLocation) {
                const res = await updateLocation(editLocation._id, locationData);
                setLocations((prev) =>
                    prev.map((loc) =>
                        loc._id === editLocation._id ? { ...res.data, _id: res.data._id } : loc
                    )
                );
                
                setSuccessMessage("Location updated successfully!");
            } else {
                const res = await addLocation(locationData);
                setLocations((prev) => [...prev, { ...res.data.data, _id: res.data.data._id || Date.now() }]);
                setSuccessMessage("Location added successfully!");
                
            }

             const refreshed = await fetchLocations();
    setLocations(refreshed.data.data);
            handleCloseModal();
        } catch (err) {
            console.error("Failed to process location:", err);
            const serverErrors = err.response?.data?.errors;
            if (serverErrors && Array.isArray(serverErrors)) {
                setApiError(serverErrors.map((e) => e.msg).join(" | "));
            } else {
                setApiError(err.response?.data?.msg || "Failed to process location. Please check console.");
            }
        } finally {
            setLoading(false);
            setTimeout(() => {
                setApiError(null);
                setSuccessMessage("");
            }, 5000);
        }
    };

    const handleCSVUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.name.endsWith('.csv')) {
            setApiError('Please upload a valid CSV file.');
            setTimeout(() => setApiError(null), 5000);
            return;
        }

        setLoading(true);
        setApiError(null); // Clear previous errors
        setSuccessMessage(""); // Clear previous success messages

        Papa.parse(file, {
            complete: async (result) => {
                const rows = result.data;

                // Validate headers
                const headers = rows[0];
                const expectedHeaders = ["Location Name", "Street", "City", "Postal Code", "Country", "Work Type"];
                if (!expectedHeaders.every((header, index) => headers[index] === header)) {
                    setApiError('Invalid CSV format. Expected headers: ' + expectedHeaders.join(', '));
                    setLoading(false);
                    setTimeout(() => setApiError(null), 5000);
                    return;
                }

                const newLocations = [];
                const errors = [];

                for (let i = 1; i < rows.length; i++) {
                    const row = rows[i];
                    // Skip empty or invalid rows
                    if (!row || row.length < 6 || row.every(cell => !cell || cell.trim() === '')) {
                        // console.log(`Skipping invalid or empty row ${i + 1}`);
                        continue;
                    }

                    const [name, street, city, postalCode, country, workType] = row.map(cell => cell ? cell.trim().replace(/^"|"$/g, '') : '');

                    // Validate required fields
                    if (!name || !street || !city || !workType) {
                        errors.push(`Row ${i + 1}: Missing required fields (name, street, city, work type)`);
                        continue;
                    }

                    // Validate workType (case-insensitive)
                    if (workType.toUpperCase() !== "SUPPORT WORKER") {
                        errors.push(`Row ${i + 1}: Invalid work type. Must be 'SUPPORT WORKER'`);
                        continue;
                    }

                    const locationData = {
                        name,
                        address: {
                            street,
                            city,
                            postalCode: postalCode || '',
                            country: country || '',
                        },
                        workType: "SUPPORT WORKER",
                        isCustom: true,
                    };

                    try {
                        const res = await addLocation(locationData);
                        if (res.status >= 200 && res.status < 300) {
                            newLocations.push({ ...res.data, _id: res.data._id || Date.now() });
                        } else {
                            errors.push(`Row ${i + 1}: Unexpected API response status ${res.status}`);
                        }
                    } catch (err) {
                        console.error(`Failed to add location at row ${i + 1}:`, {
                            message: err.message,
                            status: err.response?.status,
                            data: err.response?.data,
                        });
                        errors.push(`Row ${i + 1}: ${err.response?.data?.msg || 'Unknown error'}`);
                    }
                }

                // Update state based on results
                if (newLocations.length > 0) {
                    setLocations((prev) => [...prev, ...newLocations]);
                    let message = `Successfully imported ${newLocations.length} location(s)`;
                    if (errors.length > 0) {
                        message += `. ${errors.length} row(s) failed: ${errors.join('; ')}`;
                        setApiError(message);
                    } else {
                        setSuccessMessage(message);
                    }
                } else {
                    setApiError(errors.length > 0 ? `No locations imported. Errors: ${errors.join('; ')}` : 'No valid locations found in the CSV file.');
                }

                setLoading(false);
                fileInputRef.current.value = ''; // Reset file input
                setTimeout(() => {
                    setApiError(null);
                    setSuccessMessage("");
                }, 8000); // Extended timeout for longer error messages
            },
            error: (err) => {
                console.error('CSV Parsing Error:', err);
                setApiError('Failed to parse CSV file. Please ensure it is formatted correctly.');
                setLoading(false);
                setTimeout(() => setApiError(null), 5000);
            },
            header: false,
            skipEmptyLines: true,
        });
    };

    const isAuthorized = user && (user.role === "admin" || user.role === "manager");
    const isAdmin = user && user.role === "admin";

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50">
            {/* Header */}
            <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200/50">
                <div className="px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                                <FiMap className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">Locations</h1>
                                <p className="text-xs text-gray-500">{filteredLocations.length} total locations</p>
                            </div>
                        </div>
                        {isAdmin && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={async () => {
                                        const { jsPDF } = await import("jspdf");
                                        const autoTable = (await import("jspdf-autotable")).default;
                                        const doc = new jsPDF();
                                        const columns = [
                                            { header: "Location Name", dataKey: "name" },
                                            { header: "Street", dataKey: "street" },
                                            { header: "City", dataKey: "city" },
                                            { header: "Postal Code", dataKey: "postalCode" },
                                            { header: "Country", dataKey: "country" },
                                            { header: "Work Type", dataKey: "workType" },
                                        ];
                                        const rows = filteredLocations.map(loc => ({
                                            name: loc.name,
                                            street: loc.address?.street || "",
                                            city: loc.address?.city || "",
                                            postalCode: loc.address?.postalCode || "",
                                            country: loc.address?.country || "",
                                            workType: loc.workType || "",
                                        }));
                                        doc.text("Locations List", 14, 16);
                                        autoTable(doc, {
                                            startY: 22,
                                            head: [columns.map(col => col.header)],
                                            body: rows.map(row => columns.map(col => row[col.dataKey])),
                                            styles: { fontSize: 10 },
                                        });
                                        doc.save("locations.pdf");
                                    }}
                                    className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors"
                                    title="Export to PDF"
                                >
                                    <FiDownload className="w-5 h-5 text-gray-600" />
                                </button>
                                <input
                                    type="file"
                                    accept=".csv"
                                    ref={fileInputRef}
                                    className="hidden"
                                    onChange={handleCSVUpload}
                                />
                                <button
                                    onClick={() => fileInputRef.current.click()}
                                    className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors"
                                    title="Upload CSV"
                                >
                                    <FiUpload className="w-5 h-5 text-gray-600" />
                                </button>
                                <button
                                    onClick={handleOpenModal}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl font-medium shadow-lg shadow-blue-500/25 transition-all hover:shadow-xl hover:shadow-blue-500/30 hover:scale-105"
                                >
                                    <FiPlus className="w-5 h-5" />
                                    <span className="hidden sm:inline">Add Location</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            {/* Main Container */}
            <div className="px-4 sm:px-6 lg:px-8 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Main Content */}
                    <main className="lg:col-span-8">
                        {/* Alerts */}
                        {successMessage && (
                            <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-4 flex items-center gap-3 animate-fadeIn">
                                <FiCheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                                <div className="flex-1">
                                    <p className="text-green-800 font-medium">Success!</p>
                                    <p className="text-green-700 text-sm">{successMessage}</p>
                                </div>
                            </div>
                        )}

                        {apiError && (
                            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-4 flex items-center gap-3 animate-fadeIn">
                                <FiX className="w-5 h-5 text-red-600 flex-shrink-0" />
                                <div className="flex-1">
                                    <p className="text-red-800 font-medium">Error!</p>
                                    <p className="text-red-700 text-sm">{apiError}</p>
                                </div>
                            </div>
                        )}

                        {/* Search */}
                        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm mb-4">
                            <div className="relative">
                                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search locations..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm"
                                />
                            </div>
                        </div>

                        {/* Locations List */}
                        {loading ? (
                            <div className="space-y-3">
                                {Array.from({ length: 5 }).map((_, idx) => (
                                    <div key={idx} className="bg-white rounded-2xl p-6 border border-gray-100 animate-pulse">
                                        <div className="flex items-center gap-4 mb-3">
                                            <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                                            <div className="flex-1">
                                                <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                                                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : filteredLocations.length === 0 ? (
                            <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
                                <div className="w-20 h-20 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                                    <FiMapPin className="w-10 h-10 text-gray-400" />
                                </div>
                                <p className="text-gray-500 text-lg font-medium">No locations found</p>
                                <p className="text-gray-400 text-sm mt-1">Add your first location to get started!</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {filteredLocations.sort((a, b) => (a.name||'').localeCompare(b.name)).map((loc) => (
                                    <div
                                        key={loc._id}
                                        className="bg-white rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-200 overflow-hidden"
                                    >
                                        <div className="p-5">
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20">
                                                        <FiMapPin className="w-6 h-6 text-white" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="font-semibold text-gray-900 text-lg truncate">{loc.name}</h3>
                                                        <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                                                            <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                                                                {loc.workType}
                                                            </span>
                                                        </p>
                                                    </div>
                                                </div>
                                                {isAdmin && (
                                                    <button
                                                        onClick={() => handleEditLocation(loc)}
                                                        className="p-2 hover:bg-green-50 rounded-full transition-colors group flex-shrink-0"
                                                        title="Edit location"
                                                    >
                                                        <FiEdit2 className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 rounded-xl p-3">
                                                <FiMapPin className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                                <p className="leading-relaxed">
                                                    {loc.address?.street}, {loc.address?.city}, {loc.address?.postalCode}, {loc.address?.country}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </main>

                    {/* Sidebar */}
                    <aside className="lg:col-span-4">
                        <div className="sticky top-24 space-y-4">
                            {/* Teams Card */}
                            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
                                <div className="flex items-center gap-2 mb-4">
                                    <FiUsers className="w-5 h-5 text-blue-600" />
                                    <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Teams</h2>
                                </div>
                                <div className="relative mb-4">
                                    <FiFilter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <select
                                        className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all text-sm appearance-none cursor-pointer"
                                        value={selectedLocation}
                                        onChange={(e) => setSelectedLocation(e.target.value)}
                                    >
                                        <option value="">All Locations</option>
                                        {locations.map((loc) => (
                                            <option key={loc._id} value={loc.name}>
                                                {loc.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    {scheduledWorkers.length > 0 ? (
                                        <>
                                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                                Scheduled Workers ({scheduledWorkers.length})
                                            </p>
                                            <div className="space-y-2 max-h-96 overflow-y-auto">
                                                {[...scheduledWorkers].sort((a, b) => a.localeCompare(b)).map((worker, index) => {
                                                    const color = getWorkerColor(worker);
                                                    return (
                                                        <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                                            <div 
                                                                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                                                                style={{
                                                                    background: `linear-gradient(to bottom right, ${color.from}, ${color.to})`
                                                                }}
                                                            >
                                                                {worker.charAt(0).toUpperCase()}
                                                            </div>
                                                            <span className="text-sm text-gray-700 font-medium">{worker}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center py-8">
                                            <div className="w-12 h-12 bg-gray-100 rounded-full mx-auto mb-3 flex items-center justify-center">
                                                <FiUsers className="w-6 h-6 text-gray-400" />
                                            </div>
                                            <p className="text-gray-500 text-sm">
                                                No workers scheduled at{" "}
                                                {selectedLocation || "any location"}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>

            {/* Add/Edit Location Modal */}
            {(isModalOpen || isEditModalOpen) && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn" onClick={handleCloseModal}>
                    <div ref={modalRef} className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-slideUp" onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-100">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                                    <FiMapPin className="w-5 h-5 text-white" />
                                </div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    {isEditModalOpen ? 'Edit Location' : 'Add New Location'}
                                </h2>
                            </div>
                            <button
                                onClick={handleCloseModal}
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <FiX className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                            {formError && (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center gap-3">
                                    <FiX className="w-5 h-5 text-red-600 flex-shrink-0" />
                                    <div>
                                        <p className="text-red-800 font-medium">Error</p>
                                        <p className="text-red-700 text-sm">{formError}</p>
                                    </div>
                                </div>
                            )}
                            {apiError && (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center gap-3">
                                    <FiX className="w-5 h-5 text-red-600 flex-shrink-0" />
                                    <div>
                                        <p className="text-red-800 font-medium">Error</p>
                                        <p className="text-red-700 text-sm">{apiError}</p>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleFormSubmit} className="space-y-5">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                                        Location Name
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={form.name}
                                        onChange={handleFormChange}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all placeholder-gray-400"
                                        placeholder="e.g., Central Office"
                                        required
                                    />
                                </div>

                                <div className="border-t border-gray-100 pt-4">
                                    <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                                        <FiMapPin className="w-4 h-4 text-blue-600" />
                                        Location Address
                                    </h3>
                                    <div className="space-y-4">
                                        <div>
                                            <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-2">
                                                Street
                                            </label>
                                            <input
                                                type="text"
                                                id="street"
                                                name="street"
                                                value={form.street}
                                                onChange={handleFormChange}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all placeholder-gray-400"
                                                placeholder="e.g., 123 Main Street"
                                                required
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                                                    City
                                                </label>
                                                <input
                                                    type="text"
                                                    id="city"
                                                    name="city"
                                                    value={form.city}
                                                    onChange={handleFormChange}
                                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all placeholder-gray-400"
                                                    placeholder="e.g., London"
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-2">
                                                    Postal Code
                                                </label>
                                                <input
                                                    type="text"
                                                    id="postalCode"
                                                    name="postalCode"
                                                    value={form.postalCode}
                                                    onChange={handleFormChange}
                                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all placeholder-gray-400"
                                                    placeholder="e.g., SW1A 1AA"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                                                Country
                                            </label>
                                            <input
                                                type="text"
                                                id="country"
                                                name="country"
                                                value={form.country}
                                                onChange={handleFormChange}
                                                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all placeholder-gray-400"
                                                placeholder="e.g., United Kingdom"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="workType" className="block text-sm font-semibold text-gray-700 mb-2">
                                        Work Type
                                    </label>
                                    <select
                                        id="workType"
                                        name="workType"
                                        value={form.workType}
                                        onChange={handleFormChange}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all appearance-none cursor-pointer"
                                        required
                                    >
                                        <option value="">Select a Work Type</option>
                                        <option value="SUPPORT WORKER">SUPPORT WORKER</option>
                                        <option value="NURSE">NURSE</option>
                                    </select>
                                </div>
                            </form>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-6 border-t border-gray-100 bg-gray-50">
                            <div className="flex gap-3">
                                {isEditModalOpen && (
                                    <button
                                        type="button"
                                        onClick={handleDeleteLocation}
                                        className="px-5 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-all shadow-lg shadow-red-500/20 hover:shadow-xl hover:shadow-red-500/30"
                                        disabled={loading}
                                    >
                                        <FiTrash2 className="w-4 h-4 inline mr-2" />
                                        Delete
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="flex-1 px-5 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all"
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        document.querySelector('form').requestSubmit();
                                    }}
                                    className="flex-1 px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/25 transition-all hover:shadow-xl hover:shadow-blue-500/30 flex items-center justify-center gap-2"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    ) : (
                                        <>
                                            <FiCheckCircle className="w-4 h-4" />
                                            {isEditModalOpen ? 'Update Location' : 'Add Location'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 animate-slideUp">
                        <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                            <FiTrash2 className="w-8 h-8 text-red-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">Delete Location?</h2>
                        <p className="text-gray-600 text-center mb-6">
                            Are you sure you want to delete "{editLocation?.name}"? This action cannot be undone.
                        </p>
                        {apiError && (
                            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center gap-3">
                                <FiX className="w-5 h-5 text-red-600 flex-shrink-0" />
                                <p className="text-red-700 text-sm">{apiError}</p>
                            </div>
                        )}
                        <div className="flex gap-3">
                            <button
                                onClick={handleCloseModal}
                                className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all"
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteLocation}
                                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl font-semibold shadow-lg shadow-red-500/25 transition-all hover:shadow-xl hover:shadow-red-500/30 flex items-center justify-center gap-2"
                                disabled={loading}
                            >
                                {loading ? (
                                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    'Delete'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add custom CSS for animations */}
            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from { 
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to { 
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
                .animate-slideUp {
                    animation: slideUp 0.3s ease-out;
                }
            `}</style>
        </div>
    );
}







// import React, { useEffect, useState, useMemo, useRef } from "react";
// import { fetchLocations, addLocation, fetchSchedulesInRange, updateLocation, deleteLocation } from "../api";
// import { MapPinIcon, MagnifyingGlassIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
// import { useAuth } from '../context/AuthContext';
// import Papa from 'papaparse';

// const getFirstName = (nameOrObject) => {
//     if (!nameOrObject) return ''; 

//     let actualObject = nameOrObject;

//     if (Array.isArray(nameOrObject) && nameOrObject.length > 0) {
//         actualObject = nameOrObject[0]; // Get the first item from the array
//     }

//     if (typeof actualObject === 'object' && actualObject !== null) {
//         if (actualObject.firstName) {
//             return `${actualObject.firstName} ${actualObject.lastName || ''}`.trim();
//         }
//         if (actualObject.name) {
//             const parts = actualObject.name.trim().split(' ');
//             return parts.length > 0 ? parts[0] : '';
//         }
//         return ''; // Object but no recognizable name property
//     }

//     if (typeof actualObject === 'string') {
//         const parts = actualObject.trim().split(' ');
//         return parts.length > 0 ? parts[0] : '';
//     }

//     return ''; // Handle any other unexpected types
// };

// const getStartOfWeek = (date) => {
//     const d = new Date(date);
//     const day = d.getDay();
//     const diff = d.getDate() - day + (day === 0 ? -6 : 1);
//     const monday = new Date(d.setDate(diff));
//     monday.setHours(0, 0, 0, 0);
//     return monday;
// };

// export default function LocationPage() {
//     const [locations, setLocations] = useState([]);
//     const [schedules, setSchedules] = useState([]);
//     const [searchTerm, setSearchTerm] = useState("");
//     const [selectedLocation, setSelectedLocation] = useState("");
//     const [isModalOpen, setIsModalOpen] = useState(false);
//     const [isEditModalOpen, setIsEditModalOpen] = useState(false);
//     const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
//     const [editLocation, setEditLocation] = useState(null);
//     const [loading, setLoading] = useState(false);
//     const [apiError, setApiError] = useState(null);
//     const [successMessage, setSuccessMessage] = useState("");
//     const { user, token, isLoading: isAuthLoading } = useAuth();
//     const fileInputRef = useRef(null);
//     const modalRef = useRef(null);

//     const [form, setForm] = useState({
//         name: '',
//         street: '',
//         city: '',
//         postalCode: '',
//         country: '',
//         workType: '',
//     });
//     const [formError, setFormError] = useState('');

//     useEffect(() => {
//         const loadLocations = async () => {
//             setLoading(true);
//             setApiError(null);
//             if (!token) {
//                 setApiError("Authentication token missing. Please log in.");
//                 setLoading(false);
//                 return;
//             }

//             try {
//                 const res = await fetchLocations();
//                 setLocations(res.data.data);
//             } catch (err) {
//                 console.error("Failed to fetch locations:", err);
//                 setApiError(
//                     err.response?.data?.message ||
//                     "Failed to load locations. Please try again."
//                 );
//             } finally {
//                 setLoading(false);
//             }
//         };

//         if (token && !isAuthLoading) {
//             loadLocations();
//         } else if (!token && !isAuthLoading) {
//             setApiError("You are not logged in. Please log in to view locations.");
//         }
//     }, [token, isAuthLoading]);

//     useEffect(() => {
//         const loadSchedules = async () => {
//             if (!token || isAuthLoading) {
//                 setSchedules([]);
//                 return;
//             }

//             try {
//                 const currentYear = new Date().getFullYear();
//                 const startOfYear = new Date(currentYear, 0, 1);
//                 const endOfYear = new Date(currentYear, 11, 31);
//                 console.log(`Fetching schedules from ${startOfYear.toISOString().split('T')[0]} to ${endOfYear.toISOString().split('T')[0]}`);
//                 const res = await fetchSchedulesInRange(startOfYear, endOfYear);
//                 setSchedules(res.data.data);
//                 console.log("Fetched schedules:", res.data);
//             } catch (err) {
//                 console.error("Failed to fetch schedules in range:", err);
//                 setApiError("Failed to load schedules. Please try again.");
//             }
//         };

//         if (!isAuthLoading && token) {
//             loadSchedules();
//         } else if (!token && !isAuthLoading) {
//             setSchedules([]);
//         }
//     }, [token, isAuthLoading]);

//    useEffect(() => {
//   const handleClickOutside = (event) => {
//     if (modalRef.current && !modalRef.current.contains(event.target)) {
//       handleCloseModal();
//     }
//   };

//   if (isModalOpen || isEditModalOpen) {
//     document.addEventListener("mousedown", handleClickOutside);
//   }

//   return () => {
//     document.removeEventListener("mousedown", handleClickOutside);
//   };
// }, [isModalOpen, isEditModalOpen]);


//     const filteredLocations = useMemo(() => {
//         return locations.filter((loc) =>
//             loc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
//             loc.address?.street.toLowerCase().includes(searchTerm.toLowerCase()) ||
//             loc.address?.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
//             loc.address?.postalCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
//             loc.address?.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
//             loc.workType.toLowerCase().includes(searchTerm.toLowerCase())
//         );
//     }, [locations, searchTerm]);

//     const scheduledWorkers = useMemo(() => {
//         console.log("Processing schedules:", schedules);
//         if (!schedules || schedules.length === 0) {
//             console.log("No schedules available");
//             return [];
//         }

//         if (!selectedLocation) {
//             const allWorkers = schedules
//                 .filter(s => s.careWorker && s.location)
//                 .map(s => ({
//                     name: getFirstName(s.careWorker),
//                     location: s.location?.name || 'Unknown'
//                 }));
//             const uniqueWorkers = [...new Set(allWorkers.map(w => w.name))].filter(name => name);
//             console.log("All unique workers across locations:", uniqueWorkers);
//             return uniqueWorkers;
//         } else {
//             const filteredSchedules = schedules.filter(s => {
//                 if (!s.location || !s.location.name) {
//                     console.warn(`Schedule missing location data:`, s);
//                     return false;
//                 }
//                 return s.location.name === selectedLocation;
//             });
//             const workersForLocation = filteredSchedules
//                 .filter(s => s.careWorker)
//                 .map(s => getFirstName(s.careWorker));
//             const uniqueWorkersForLocation = [...new Set(workersForLocation)].filter(name => name);
//             console.log(`Unique workers for ${selectedLocation}:`, uniqueWorkersForLocation);
//             return uniqueWorkersForLocation;
//         }
//     }, [schedules, selectedLocation]);

//     const handleOpenModal = () => {
//         setIsModalOpen(true);
//         setApiError(null);
//         setSuccessMessage("");
//         setFormError("");
//         setForm({
//             name: '',
//             street: '',
//             city: '',
//             postalCode: '',
//             country: '',
//             workType: '',
//         });
//         setEditLocation(null);
//     };

//     const handleEditLocation = (location) => {
//         setIsEditModalOpen(true);
//         setApiError(null);
//         setSuccessMessage("");
//         setFormError("");
//         setForm({
//             name: location.name || '',
//             street: location.address?.street || '',
//             city: location.address?.city || '',
//             postalCode: location.address?.postalCode || '',
//             country: location.address?.country || '',
//             workType: location.workType || '',
//         });
//         setEditLocation(location);
//     };

//     const handleCloseModal = () => {
//         setIsModalOpen(false);
//         setIsEditModalOpen(false);
//         setIsDeleteModalOpen(false);
//         setApiError(null);
//         setFormError('');
//         setEditLocation(null);
//     };

//     const handleFormChange = (e) => {
//         const { name, value } = e.target;
//         setForm((prev) => ({ ...prev, [name]: value }));
//     };

//     const handleDeleteLocation = async () => {
//         if (!editLocation) return;
//         if (!token) {
//             setApiError("You must be logged in to delete a location.");
//             return;
//         }

//         setIsDeleteModalOpen(true);
//     };

//     const confirmDeleteLocation = async () => {
//         setLoading(true);
//         setApiError(null);

//         try {
//             await deleteLocation(editLocation._id);
//             setLocations((prev) => prev.filter((loc) => loc._id !== editLocation._id));
//             setSuccessMessage("Location deleted successfully!");
//             handleCloseModal();
//         } catch (err) {
//             console.error("Failed to delete location:", {
//                 message: err.message,
//                 status: err.response?.status,
//                 data: err.response?.data,
//             });
//             setApiError(err.response?.data?.msg || "Failed to delete location. Please try again.");
//         } finally {
//             setLoading(false);
//             setTimeout(() => {
//                 setApiError(null);
//                 setSuccessMessage("");
//             }, 5000);
//         }
//     };

//     const handleFormSubmit = async (e) => {
//         e.preventDefault();
//         setFormError('');
//         setApiError(null);
//         if (!token) {
//             setApiError("You must be logged in to add or update a location.");
//             setLoading(false);
//             return;
//         }

//         if (!form.name || !form.street || !form.city || !form.postalCode || !form.country || !form.workType) {
//             if (!form.workType) {
//                 setFormError('Work type is required.');
//             } else {
//                 setFormError('All fields are required.');
//             }
//             return;
//         }

//         setLoading(true);

//         const locationData = {
//             name: form.name,
//             address: {
//                 street: form.street,
//                 city: form.city,
//                 postalCode: form.postalCode,
//                 country: form.country,
//             },
//             workType: form.workType,
//             isCustom: true,
//         };

//         console.log("Submitting Location:", locationData);

//         try {
//             if (editLocation) {
//                 const res = await updateLocation(editLocation._id, locationData);
//                 setLocations((prev) =>
//                     prev.map((loc) =>
//                         loc._id === editLocation._id ? { ...res.data, _id: res.data._id } : loc
//                     )
//                 );
//                 setSuccessMessage("Location updated successfully!");
//             } else {
//                 const res = await addLocation(locationData);
//                 setLocations((prev) => [...prev, { ...res.data.data, _id: res.data.data._id || Date.now() }]);
//                 setSuccessMessage("Location added successfully!");
//             }
//             handleCloseModal();
//         } catch (err) {
//             console.error("Failed to process location:", err);
//             const serverErrors = err.response?.data?.errors;
//             if (serverErrors && Array.isArray(serverErrors)) {
//                 setApiError(serverErrors.map((e) => e.msg).join(" | "));
//             } else {
//                 setApiError(err.response?.data?.msg || "Failed to process location. Please check console.");
//             }
//         } finally {
//             setLoading(false);
//             setTimeout(() => {
//                 setApiError(null);
//                 setSuccessMessage("");
//             }, 5000);
//         }
//     };

//     const handleCSVUpload = async (event) => {
//         const file = event.target.files[0];
//         if (!file) return;

//         if (!file.name.endsWith('.csv')) {
//             setApiError('Please upload a valid CSV file.');
//             setTimeout(() => setApiError(null), 5000);
//             return;
//         }

//         setLoading(true);
//         setApiError(null); // Clear previous errors
//         setSuccessMessage(""); // Clear previous success messages

//         Papa.parse(file, {
//             complete: async (result) => {
//                 const rows = result.data;
//                 console.log('Parsed CSV Rows:', rows); // Debug entire CSV

//                 // Validate headers
//                 const headers = rows[0];
//                 const expectedHeaders = ["Location Name", "Street", "City", "Postal Code", "Country", "Work Type"];
//                 if (!expectedHeaders.every((header, index) => headers[index] === header)) {
//                     setApiError('Invalid CSV format. Expected headers: ' + expectedHeaders.join(', '));
//                     setLoading(false);
//                     setTimeout(() => setApiError(null), 5000);
//                     return;
//                 }

//                 const newLocations = [];
//                 const errors = [];

//                 for (let i = 1; i < rows.length; i++) {
//                     const row = rows[i];
//                     // Skip empty or invalid rows
//                     if (!row || row.length < 6 || row.every(cell => !cell || cell.trim() === '')) {
//                         console.log(`Skipping invalid or empty row ${i + 1}`);
//                         continue;
//                     }

//                     const [name, street, city, postalCode, country, workType] = row.map(cell => cell ? cell.trim().replace(/^"|"$/g, '') : '');
//                     console.log(`Processing row ${i + 1}:`, { name, street, city, postalCode, country, workType }); // Debug row data

//                     // Validate required fields
//                     if (!name || !street || !city || !workType) {
//                         errors.push(`Row ${i + 1}: Missing required fields (name, street, city, work type)`);
//                         console.log(`Validation failed for row ${i + 1}: Missing fields`);
//                         continue;
//                     }

//                     // Validate workType (case-insensitive)
//                     if (workType.toUpperCase() !== "SUPPORT WORKER") {
//                         errors.push(`Row ${i + 1}: Invalid work type. Must be 'SUPPORT WORKER'`);
//                         console.log(`Invalid workType for row ${i + 1}: ${workType}`);
//                         continue;
//                     }

//                     const locationData = {
//                         name,
//                         address: {
//                             street,
//                             city,
//                             postalCode: postalCode || '',
//                             country: country || '',
//                         },
//                         workType: "SUPPORT WORKER",
//                         isCustom: true,
//                     };

//                     try {
//                         const res = await addLocation(locationData);
//                         console.log(`API Response for row ${i + 1}:`, { status: res.status, data: res.data }); // Debug API response
//                         if (res.status >= 200 && res.status < 300) {
//                             newLocations.push({ ...res.data, _id: res.data._id || Date.now() });
//                         } else {
//                             errors.push(`Row ${i + 1}: Unexpected API response status ${res.status}`);
//                         }
//                     } catch (err) {
//                         console.error(`Failed to add location at row ${i + 1}:`, {
//                             message: err.message,
//                             status: err.response?.status,
//                             data: err.response?.data,
//                         });
//                         errors.push(`Row ${i + 1}: ${err.response?.data?.msg || 'Unknown error'}`);
//                     }
//                 }

//                 // Update state based on results
//                 if (newLocations.length > 0) {
//                     setLocations((prev) => [...prev, ...newLocations]);
//                     let message = `Successfully imported ${newLocations.length} location(s)`;
//                     if (errors.length > 0) {
//                         message += `. ${errors.length} row(s) failed: ${errors.join('; ')}`;
//                         setApiError(message);
//                     } else {
//                         setSuccessMessage(message);
//                     }
//                 } else {
//                     setApiError(errors.length > 0 ? `No locations imported. Errors: ${errors.join('; ')}` : 'No valid locations found in the CSV file.');
//                 }

//                 setLoading(false);
//                 fileInputRef.current.value = ''; // Reset file input
//                 setTimeout(() => {
//                     setApiError(null);
//                     setSuccessMessage("");
//                 }, 8000); // Extended timeout for longer error messages
//             },
//             error: (err) => {
//                 console.error('CSV Parsing Error:', err);
//                 setApiError('Failed to parse CSV file. Please ensure it is formatted correctly.');
//                 setLoading(false);
//                 setTimeout(() => setApiError(null), 5000);
//             },
//             header: false,
//             skipEmptyLines: true,
//         });
//     };

//     const isAuthorized = user && (user.role === "admin" || user.role === "manager");
//     const isAdmin = user && user.role === "admin";

//     return (
//         <div className="flex flex-col h-screen bg-gray-100 font-sans">
//             <div className="flex flex-1 flex-col md:flex-row">
//                 <div className="flex-1 px-4 py-4 md:px-10 md:py-6 bg-white overflow-auto">

//                      <div className="mb-4 md:mb-6">
//                     <h1 className="text-2xl font-semibold text-gray-800">Locations</h1>
//                     <p className="text-sm text-gray-600">Showing {filteredLocations.length} Locations</p>
//                 </div>

//                     {successMessage && (
//                         <div
//                             className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4 text-sm"
//                             role="alert"
//                         >
//                             <p className="font-bold">Success!</p>
//                             <p>{successMessage}</p>
//                         </div>
//                     )}

//                     {apiError && (
//                         <div
//                             className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 text-sm"
//                             role="alert"
//                         >
//                             <p className="font-bold">Error!</p>
//                             <p>{apiError}</p>
//                         </div>
//                     )}

//                     <div className="flex flex-col md:flex-row items-center justify-between mb-4 space-y-4 md:space-y-0">
//                         <div className="relative w-full md:w-1/3">
//                             <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
//                             <input
//                                 type="text"
//                                 placeholder="Search..."
//                                 className="border border-gray-300 pl-10 pr-3 py-2 rounded-md w-full text-sm focus:ring-indigo-500 focus:border-indigo-500"
//                                 value={searchTerm}
//                                 onChange={(e) => setSearchTerm(e.target.value)}
//                             />
//                         </div>
//                         {isAdmin && (
//                             <div className="flex items-center w-full md:w-auto">
//                                 <button
//                                     className="p-2 flex items-center justify-center  transition duration-200"
//                                     onClick={async () => {
//                                         const { jsPDF } = await import("jspdf");
//                                         const autoTable = (await import("jspdf-autotable")).default;

//                                         const doc = new jsPDF();
//                                         const columns = [
//                                             { header: "Location Name", dataKey: "name" },
//                                             { header: "Street", dataKey: "street" },
//                                             { header: "City", dataKey: "city" },
//                                             { header: "Postal Code", dataKey: "postalCode" },
//                                             { header: "Country", dataKey: "country" },
//                                             { header: "Work Type", dataKey: "workType" },
//                                         ];
//                                         const rows = filteredLocations.map(loc => ({
//                                             name: loc.name,
//                                             street: loc.address?.street || "",
//                                             city: loc.address?.city || "",
//                                             postalCode: loc.address?.postalCode || "",
//                                             country: loc.address?.country || "",
//                                             workType: loc.workType || "",
//                                         }));

//                                         doc.text("Locations List", 14, 16);
//                                         autoTable(doc, {
//                                             startY: 22,
//                                             head: [columns.map(col => col.header)],
//                                             body: rows.map(row => columns.map(col => row[col.dataKey])),
//                                             styles: { fontSize: 10 },
//                                         });

//                                         doc.save("locations.pdf");
//                                     }}
//                                 >
//                                     <span>
//                                         <img src="/exporticon.svg" alt="Export icon" className='w-[20px] mr-0.5' title="Export"/>
//                                     </span>
//                                     {/* <span className="font-small">Export</span> */}
//                                 </button>
//                                 <div>
//                                     <input
//                                         type="file"
//                                         accept=".csv"
//                                         ref={fileInputRef}
//                                         className="hidden"
//                                         onChange={handleCSVUpload}
//                                     />
//                                     <button
//                                         className=" rounded-md p-2 flex items-center justify-center  transition duration-200"
//                                         onClick={() => fileInputRef.current.click()}
//                                     >
//                                         {/* <ArrowUpTrayIcon className="w-[20px] mr-1" /> */}
//                                         <img src="/uploadicon.svg" alt="Upload icon" className='w-auto mr-0.5' title="Upload(.csv)"/>
//                                         {/* <span className="font-small">Upload CSV</span> */}
//                                     </button>
//                                 </div>
//                                  <button
//                                 className="w-full md:w-auto bg-blue-600 text-white px-3 py-1 rounded-md flex items-center justify-center space-x-2 hover:bg-blue-600 transition duration-200"
//                                 onClick={handleOpenModal}
//                             >
//                                 <span className="text-xl">+</span>
//                                 <span>Add Location</span>
//                             </button>
//                             </div>
//                         )}
//                     </div>

                    

//                     <div className="hidden md:grid grid-cols-[2fr,3fr,1.5fr,1.5fr] gap-4 text-sm font-semibold text-gray-600 py-3 border-b border-gray-200">
//                         <div>Location Name</div>
//                         <div>Location Address</div>
//                         <div>Areas</div>
//                         {isAdmin && (
//                             <div className="text-right">Actions</div>
//                         )}
//                     </div>

//                     {loading && <p className="py-4 text-center text-gray-600">Loading locations...</p>}
//                     {!loading && filteredLocations.length === 0 && (
//                         <p className="py-4 text-center text-gray-600">No locations found.</p>
//                     )}
//                     {!loading &&
//                         filteredLocations.sort((a, b) => a.name.localeCompare(b.name)).map((loc) => (
//                             <div
//                                 key={loc._id}
//                                 className="flex flex-col md:grid md:grid-cols-[2fr,3fr,1.5fr,1.5fr] gap-4 py-3 items-start md:items-center border-b border-gray-200 text-sm"
//                             >
//                                 <div className="text-gray-900 font-medium">{loc.name}</div>
//                                 <div className="text-[#0034AD] flex items-center mt-2 md:mt-0">
//                                     <MapPinIcon className="h-4 w-4 mr-1 flex-shrink-0" />
//                                     <span className="">
//                                         {loc.address?.street}, {loc.address?.city}, {loc.address?.postalCode}, {loc.address?.country}
//                                     </span>
//                                 </div>
//                                 <div className="text-gray-700 mt-2 md:mt-0">{loc.workType}</div>
//                                 <div className="flex justify-start md:justify-end w-full mt-3 md:mt-0">
//                                     {isAuthorized && isAdmin && (
//                                         <button
//                                             className="inline-flex justify-center w-full md:w-auto rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//                                             onClick={() => handleEditLocation(loc)}
//                                         >
//                                             Edit Settings
//                                             <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
//                                                 <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
//                                             </svg>
//                                         </button>
//                                     )}
//                                 </div>
//                             </div>
//                         ))}
//                 </div>

//                 <div className="w-full md:w-72 bg-gray-50 border-t md:border-t-0 md:border-l border-gray-200 px-4 py-4 md:px-5 md:py-6 shadow-md flex-shrink-0">
//                     <div className="bg-white rounded-lg shadow-sm p-4">
//                         <h2 className="text-lg font-semibold text-gray-800 mb-3">Teams</h2>
//                         <select
//                             className="w-full border border-gray-300 px-3 py-2 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
//                             value={selectedLocation}
//                             onChange={(e) => setSelectedLocation(e.target.value)}
//                         >
//                             <option value="">All Locations</option>
//                             {locations.map((loc) => (
//                                 <option key={loc._id} value={loc.name}>
//                                     {loc.name}
//                                 </option>
//                             ))}
//                         </select>
//                         <div className="mt-6 md:mt-10 text-sm text-gray-700">
//                             {scheduledWorkers.length > 0 ? (
//                                 <>
//                                     <p className="font-semibold mb-2">
//                                         Scheduled Workers at {selectedLocation || "All Locations"}:
//                                     </p>
//                                     <ul className="list-none list-inside space-y-1">
//                                         {scheduledWorkers.map((worker, index) => (
//                                             <li key={index} className="pt-[10px]">{worker}</li>
//                                         ))}
//                                     </ul>
//                                 </>
//                             ) : (
//                                 <p className="text-gray-500">
//                                     No workers are currently scheduled at{" "}
//                                     {selectedLocation || "any locations"}.
//                                 </p>
//                             )}
//                         </div>
//                     </div>
//                 </div>
//             </div>

//             {(isModalOpen || isEditModalOpen) && (
//                 <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
//                     <div ref={modalRef} className="bg-white rounded-xl shadow-2xl w-full max-w-sm md:max-w-md p-6 md:p-8 transform transition-all duration-300 ease-out scale-95 opacity-0 animate-scale-in">
//                         <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6 text-center">
//                             {isEditModalOpen ? 'Edit Location' : 'Add New Location'}
//                         </h2>

//                         {formError && (
//                             <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 text-sm" role="alert">
//                                 <strong className="font-bold">Error: </strong>
//                                 <span className="block sm:inline">{formError}</span>
//                             </div>
//                         )}
//                         {apiError && (
//                             <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 text-sm" role="alert">
//                                 <strong className="font-bold">Error: </strong>
//                                 <span className="block sm:inline">{apiError}</span>
//                             </div>
//                         )}

//                         <form onSubmit={handleFormSubmit} className="space-y-4 md:space-y-5">
//                             <div>
//                                 <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
//                                     Location Name
//                                 </label>
//                                 <input
//                                     type="text"
//                                     id="name"
//                                     name="name"
//                                     value={form.name}
//                                     onChange={handleFormChange}
//                                     className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm placeholder-gray-400"
//                                     placeholder="e.g., Central Office"
//                                     required
//                                 />
//                             </div>

//                             <div className="space-y-3 md:space-y-4 pt-2">
//                                 <label className="block text-base md:text-lg font-semibold text-gray-800 border-b pb-2 mb-3">
//                                     Location Address
//                                 </label>
//                                 <div>
//                                     <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">
//                                         Street
//                                     </label>
//                                     <input
//                                         type="text"
//                                         id="street"
//                                         name="street"
//                                         value={form.street}
//                                         onChange={handleFormChange}
//                                         className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm placeholder-gray-400"
//                                         placeholder="e.g., 123 Main Street"
//                                         required
//                                     />
//                                 </div>
//                                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
//                                     <div>
//                                         <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
//                                             City
//                                         </label>
//                                         <input
//                                             type="text"
//                                             id="city"
//                                             name="city"
//                                             value={form.city}
//                                             onChange={handleFormChange}
//                                             className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm placeholder-gray-400"
//                                             placeholder="e.g., London"
//                                             required
//                                         />
//                                     </div>
//                                     <div>
//                                         <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
//                                             Postal Code
//                                         </label>
//                                         <input
//                                             type="text"
//                                             id="postalCode"
//                                             name="postalCode"
//                                             value={form.postalCode}
//                                             onChange={handleFormChange}
//                                             className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm placeholder-gray-400"
//                                             placeholder="e.g., SW1A 1AA"
//                                             required
//                                         />
//                                     </div>
//                                 </div>
//                                 <div>
//                                     <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
//                                         Country
//                                     </label>
//                                     <input
//                                         type="text"
//                                         id="country"
//                                         name="country"
//                                         value={form.country}
//                                         onChange={handleFormChange}
//                                         className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm placeholder-gray-400"
//                                         placeholder="e.g., United Kingdom"
//                                         required
//                                     />
//                                 </div>
//                             </div>

//                             <div>
//                                 <label htmlFor="workType" className="block text-sm font-medium text-gray-700 mb-1">
//                                     Work Type
//                                 </label>
//                                 <select
//                                     id="workType"
//                                     name="workType"
//                                     value={form.workType}
//                                     onChange={handleFormChange}
//                                     className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
//                                     required
//                                 >
//                                     <option value="">Select a Work Type</option>
//                                     <option value="SUPPORT WORKER">SUPPORT WORKER</option>
//                                 </select>
//                             </div>

//                             <div className="flex justify-end space-x-2 pt-4 md:pt-6">
//                                 {isEditModalOpen && (
//                                     <button
//                                         type="button"
//                                         onClick={handleDeleteLocation}
//                                         className="px-4 py-2 border bg-red-600 border-gray-300 rounded-lg text-white hover:bg-red-700 transition duration-200 text-base font-medium"
//                                         disabled={loading}
//                                     >
//                                         Delete
//                                     </button>
//                                 )}
//                                 <button
//                                     type="button"
//                                     onClick={handleCloseModal}
//                                     className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition duration-200 text-base font-medium"
//                                     disabled={loading}
//                                 >
//                                     Cancel
//                                 </button>
//                                 <button
//                                     type="submit"
//                                     className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 text-base font-medium shadow-md flex items-center justify-center"
//                                     disabled={loading}
//                                 >
//                                     {loading ? (
//                                         <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                                         </svg>
//                                     ) : (
//                                         isEditModalOpen ? 'Update Location' : 'Add Location'
//                                     )}
//                                 </button>
//                             </div>
//                         </form>
//                     </div>
//                 </div>
//             )}

//             {isDeleteModalOpen && (
//                 <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
//                     <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm md:max-w-md p-6 md:p-8 transform transition-all duration-300 ease-out scale-95 opacity-0 animate-scale-in">
//                         <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6 text-center">
//                             Confirm Deletion
//                         </h2>
//                         <p className="text-sm text-gray-700 mb-6 text-center">
//                             Are you sure you want to delete the location "{editLocation?.name}"? This action cannot be undone.
//                         </p>
//                         {apiError && (
//                             <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 text-sm" role="alert">
//                                 <strong className="font-bold">Error: </strong>
//                                 <span className="block sm:inline">{apiError}</span>
//                             </div>
//                         )}
//                         <div className="flex justify-end space-x-2">
//                             <button
//                                 type="button"
//                                 onClick={handleCloseModal}
//                                 className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition duration-200 text-base font-medium"
//                                 disabled={loading}
//                             >
//                                 Cancel
//                             </button>
//                             <button
//                                 type="button"
//                                 onClick={confirmDeleteLocation}
//                                 className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 text-base font-medium flex items-center justify-center"
//                                 disabled={loading}
//                             >
//                                 {loading ? (
//                                     <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                                         <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                                         <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                                     </svg>
//                                 ) : (
//                                     'Delete'
//                                 )}
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             )}
//         </div>
//     );
// }
